// @ts-nocheck
import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';

/**
 * A custom element that displays a progress bar showing cart tier status.
 * Updates dynamically as items are added/removed from cart.
 * 
 * @extends {Component}
 */
class CartTierProgressComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    // Listen for cart updates
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    
    // Initial update - run immediately since component starts hidden
    this.#updateProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
  }

  /**
   * Handle cart update events with debouncing
   */
  #updateTimeout = null;
  #handleCartUpdate = async () => {
    // Clear any pending updates
    if (this.#updateTimeout) {
      clearTimeout(this.#updateTimeout);
    }
    
    // Debounce updates to prevent rapid consecutive calls
    // Delay ensures cart is updated on server
    this.#updateTimeout = setTimeout(async () => {
      await this.#updateProgress();
      this.#updateTimeout = null;
    }, 200);
  }

  /**
   * Store current progress to prevent unnecessary updates
   */
  #currentProgress = 0;
  
  /**
   * Store last camera count to detect changes
   */
  #lastCameraCount = 0;
  
  /**
   * Store last gift count to detect changes
   */
  #lastGiftCount = 0;

  /**
   * Update progress bar based on current cart
   */
  async #updateProgress() {
    try {
      // Add timeout protection to prevent hanging
      const cartPromise = this.#fetchCart();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Progress update timeout')), 5000)
      );
      
      const cart = await Promise.race([cartPromise, timeoutPromise]);
      const analysis = this.#analyzeCart(cart);
      
      // Hide if cart is empty
      if (cart.item_count === 0) {
        this.style.display = 'none';
        this.#currentProgress = 0;
        this.#lastCameraCount = 0;
        this.#lastGiftCount = 0;
        return;
      }
      
      // Show component
      this.style.display = '';
      
      // Determine what needs updating
      const cameraCountChanged = analysis.cameraCount !== this.#lastCameraCount;
      const giftCountChanged = analysis.giftCount !== this.#lastGiftCount;
      
      // Always update message (it can change based on both cameras and gifts)
      this.#updateMessage(analysis);
      
      // Update milestones when camera OR gift count changes
      if (cameraCountChanged || giftCountChanged) {
        this.#updateMilestones(analysis);
      }
      
      // ONLY update progress bar when camera count changes (not gifts)
      if (cameraCountChanged) {
        console.log('📊 Progress bar update: Camera count changed from', this.#lastCameraCount, 'to', analysis.cameraCount, '- Progress:', analysis.progress + '%');
        this.#updateProgressBar(analysis);
        this.#lastCameraCount = analysis.cameraCount;
      }
      
      // Update gift count tracking
      if (giftCountChanged) {
        console.log('🎁 Gift count changed from', this.#lastGiftCount, 'to', analysis.giftCount);
        this.#lastGiftCount = analysis.giftCount;
      }
      
    } catch (error) {
      console.error('Error updating progress:', error);
      // Update message to show error state instead of stuck loading
      const messageElement = this.querySelector('[data-message-text]');
      if (messageElement) {
        messageElement.textContent = 'Add items to your cart';
      }
      // Hide component on persistent errors to prevent stuck states
      setTimeout(() => {
        this.style.display = 'none';
      }, 2000);
    }
  }

  /**
   * Fetch current cart data with timeout protection
   */
  async #fetchCart() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    try {
      const response = await fetch('/cart.js', { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Analyze cart to determine tier
   * @param {any} cart
   */
  #analyzeCart(cart) {
    let cameraCount = 0;
    let giftCount = 0;
    
    // Count cameras and gifts
    cart.items.forEach(/** @param {any} item */ item => {
      if (this.#isCamera(item)) {
        cameraCount += item.quantity;
      } else if (this.#isGift(item)) {
        giftCount += item.quantity;
      }
    });
    
    // Determine tier and progress
    // Progress is ONLY based on camera count: 0% → 30% → 60% → 100%
    // Checkmarks appear when gifts/rewards are added
    let tier = 0;
    let progress = 0;
    let nextMilestone = '';
    let message = '';
    
    // Progress bar is purely camera-based
    if (cameraCount === 0) {
      progress = 0;
    } else if (cameraCount === 1) {
      progress = 30;
    } else if (cameraCount === 2) {
      progress = 60;
    } else if (cameraCount >= 3) {
      progress = 100;
    }
    
    // Messages are based on camera + gift state
    if (cameraCount === 0) {
      tier = 0;
      nextMilestone = '1 camera for free gift';
      message = `Add 1 camera to unlock a free gift!`;
    } else if (cameraCount === 1) {
      tier = 1;
      if (giftCount >= 1) {
        nextMilestone = '2nd camera';
        message = `Add another camera to unlock 2 more free gifts!`;
      } else {
        nextMilestone = 'free gift';
        message = `You've unlocked a free gift!`;
      }
    } else if (cameraCount === 2) {
      tier = 2;
      if (giftCount >= 3) {
        nextMilestone = '3rd camera';
        message = `Add one more camera to unlock a free 4th camera!`;
      } else {
        const remaining = 3 - giftCount;
        nextMilestone = `${remaining} gift${remaining > 1 ? 's' : ''}`;
        message = `You've unlocked 3 free gifts! Add ${remaining} more to cart.`;
      }
    } else if (cameraCount >= 3) {
      tier = 3;
      if (cameraCount >= 4) {
        message = `All rewards unlocked!`;
      } else {
        nextMilestone = 'free camera';
        message = `You've unlocked a 4th camera for free!`;
      }
    }
    
    return {
      tier,
      cameraCount,
      giftCount,
      progress,
      nextMilestone,
      message,
      totalPrice: cart.total_price
    };
  }

  /**
   * Check if item is a camera (by checking product handle)
   * @param {any} item
   */
  #isCamera(item) {
    // Check product handle for specific camera handles
    const handle = item.handle?.toLowerCase() || '';
    const cameraHandles = ['flashback-one35-v2-camera', 'flashback-one35-v1-camera'];
    
    if (cameraHandles.some(cameraHandle => handle.includes(cameraHandle))) {
      return true;
    }
    
    // SECONDARY: Check product title (must contain "one35" to avoid false positives)
    const title = item.product_title?.toLowerCase() || '';
    return title.includes('one35') && title.includes('camera');
  }

  /**
   * Check if item is a gift
   * @param {any} item
   */
  #isGift(item) {
    // Check by product handle
    const handle = item.handle?.toLowerCase() || '';
    const giftHandles = ['flashback-dad-cap', 'flashback-camera-lanyard', 'premium-vegan-leather-case'];
    
    if (giftHandles.some(giftHandle => handle === giftHandle)) {
      return true;
    }
    
    // Gifts are free (price = 0) and NOT cameras
    return (item.price === 0 || item.final_price === 0) && !this.#isCamera(item);
  }

  /**
   * Update the message text
   * @param {any} analysis
   */
  #updateMessage(analysis) {
    const messageElement = this.querySelector('[data-message-text]');
    if (messageElement && analysis.message) {
      messageElement.textContent = analysis.message;
    }
  }

  /**
   * Update the progress bar fill
   * @param {any} analysis
   */
  #updateProgressBar(analysis) {
    const fillElement = this.querySelector('[data-progress-fill]');
    if (!fillElement) return;
    
    const targetWidth = analysis.progress;
    
    // Skip if no actual change needed
    if (targetWidth === this.#currentProgress) {
      return;
    }
    
    // If this is the first meaningful update (going from 0 to something)
    const isFirstLoad = this.#currentProgress === 0 && targetWidth > 0;
    
    if (isFirstLoad) {
      // Disable transition for instant initial set to avoid animation from 0
      fillElement.style.transition = 'none';
      fillElement.style.width = `${targetWidth}%`;
      this.#currentProgress = targetWidth;
      
      // Re-enable transitions after a frame
      requestAnimationFrame(() => {
        fillElement.style.transition = 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
      });
    } else {
      // Smooth animated update for subsequent changes
      fillElement.style.transition = 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
      fillElement.style.width = `${targetWidth}%`;
      this.#currentProgress = targetWidth;
    }
  }

  /**
   * Update milestone states (optimized to prevent flickering)
   * @param {any} analysis
   */
  #updateMilestones(analysis) {
    // Helper to set milestone state efficiently without causing reflows/repaints
    const setMilestoneState = (milestoneNum, shouldBeActive, shouldBeCompleted) => {
      const dot = this.querySelector(`[data-milestone-dot="${milestoneNum}"]`);
      if (!dot) return;
      
      const hasCompleted = dot.classList.contains('completed');
      const hasActive = dot.classList.contains('active');
      
      // Batch DOM updates to prevent flickering
      if (shouldBeCompleted && !hasCompleted) {
        // Transition to completed
        dot.classList.add('completed');
        dot.classList.remove('active');
      } else if (shouldBeActive && !hasActive && !hasCompleted) {
        // Transition to active (only if not already completed)
        dot.classList.add('active');
      } else if (!shouldBeActive && !shouldBeCompleted && (hasActive || hasCompleted)) {
        // Reset to inactive
        dot.classList.remove('active', 'completed');
      }
    };
    
    // Milestone 1: 1 camera = free gift
    const m1Completed = analysis.cameraCount >= 1 && analysis.giftCount >= 1;
    const m1Active = analysis.cameraCount >= 1 && !m1Completed;
    setMilestoneState(1, m1Active, m1Completed);
    
    // Milestone 2: 2 cameras = 3 free gifts
    const m2Completed = analysis.cameraCount >= 2 && analysis.giftCount >= 3;
    const m2Active = analysis.cameraCount >= 2 && !m2Completed;
    setMilestoneState(2, m2Active, m2Completed);
    
    // Milestone 3: 3 cameras = free 4th camera
    const m3Completed = analysis.cameraCount >= 4;
    const m3Active = analysis.cameraCount >= 3 && !m3Completed;
    setMilestoneState(3, m3Active, m3Completed);
  }
}

if (!customElements.get('cart-tier-progress-component')) {
  customElements.define('cart-tier-progress-component', CartTierProgressComponent);
}

